import { cn } from '@heidi/utils';
import { useEffect, useRef, useState, type JSX } from 'react';

type Phase = 'blocks' | 'collapsing' | 'summary';

/** Height of the CollapsedSourcesSummary header row (p-2 button + outer p-2 wrapper) */
const SUMMARY_HEIGHT_PX = 56;
/** Duration for the collapse animation (ms) */
const COLLAPSE_DURATION_MS = 300;

interface SearchGroupTransitionProps {
  /** Whether every search in the group has finished */
  allComplete: boolean;
  /** The individual InlineSearchBlock elements in their bordered wrapper */
  searchBlocks: React.ReactNode;
  /** The CollapsedSourcesSummary element */
  summary: React.ReactNode;
}

/**
 * Manages a two-phase transition from a multi-row search group to a
 * single-row CollapsedSourcesSummary:
 *
 * 1. **blocks** — renders search blocks at natural height
 * 2. **collapsing** — animates max-height down + fades out (300ms)
 * 3. **summary** — renders CollapsedSourcesSummary (has its own fade-in)
 */
export const SearchGroupTransition = ({
  allComplete,
  searchBlocks,
  summary,
}: SearchGroupTransitionProps): JSX.Element => {
  // If all searches are already complete on mount (history load or timeline rebuild),
  // skip the animation and show the summary immediately.
  const [phase, setPhase] = useState<Phase>(allComplete ? 'summary' : 'blocks');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasStartedRef = useRef(allComplete);

  useEffect(() => {
    if (allComplete && !hasStartedRef.current) {
      hasStartedRef.current = true;
      setPhase('collapsing');
      timerRef.current = setTimeout(() => {
        setPhase('summary');
        timerRef.current = null;
      }, COLLAPSE_DURATION_MS);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [allComplete]);

  // Summary phase — render the collapsed summary directly (it has its own fade-in)
  if (phase === 'summary') {
    return <>{summary}</>;
  }

  return (
    <div
      className={cn(
        'w-full overflow-hidden transition-[max-height,opacity] ease-out',
        phase === 'collapsing' && 'opacity-0'
      )}
      style={{
        maxHeight: phase === 'collapsing' ? `${SUMMARY_HEIGHT_PX}px` : '1000px',
        transitionDuration: `${COLLAPSE_DURATION_MS}ms`,
      }}
    >
      {searchBlocks}
    </div>
  );
};
